import React, { useEffect, useMemo } from 'react';
import {
  Checkbox,
  FormHelperText,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { DropdownOptionType, DropdownPropsType, DropdownOptionTypeObjet } from './Type';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

export function SBDropdown(props: DropdownPropsType): JSX.Element {
  const theme = useTheme();

  const MenuProps = useMemo(
    () => ({
      PaperProps: {
        sx: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,

          // ✅ Dynamic width
          minWidth: 260,
          width: 'fit-content',
          maxWidth: 500,

          // ✅ Dropdown select ke width se chhota na ho
          '& .MuiMenu-list': {
            minWidth: '100%',
          },

          backdropFilter: 'blur(16px)',
          backgroundColor: alpha(theme.palette.common.white, 0.85),
          border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
          borderRadius: '12px',
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
        },
      },

      MenuListProps: {
        sx: {
          borderRadius: '12px',
          py: 0.5,
        },
      },
    }),
    [theme],
  );

  const {
    placeholder,
    id,
    multiple,
    label,
    options,
    defaultOpen,
    defaultValue,
    required,
    name,
    value,
    disabled,
    showSelectAll = multiple ? (props.showSelectAll === undefined ? true : props.showSelectAll) : props.showSelectAll,
    sx,
    isSortingAllowed = true,
    isSorted,
  } = props;
  const { onChange, renderValue } = props;
  const [selectedValue, setSelectedValue] = React.useState<string[]>([]);
  const [optionsObject, setOptionsObject] = React.useState<DropdownOptionTypeObjet>({});
  const [selectAll, setSelectAll] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState<boolean>(false);

  useEffect(() => {
    if (value) setSelectedValue(value);
    const valueArr = value?.filter(i => i !== '');
    setSelectAll(options.length === valueArr?.length);
  }, [value, options?.length]);

  useEffect(() => {
    setOptionsObject(
      options.reduce(
        (op: DropdownOptionTypeObjet, option: DropdownOptionType) => ((op[option.value] = option), op),
        {},
      ),
    );
  }, [options]);

  const handleChange = (event: SelectChangeEvent<typeof selectedValue>, child: React.ReactNode): void => {
    const {
      target: { value },
    } = event;
    // On autofill we get a stringified value.
    let values: string[] | [];
    if (showSelectAll && typeof value === 'object' && value.some(val => val === 'selectAll')) {
      if (selectAll) {
        values = [];
        setSelectAll(false);
      } else {
        values = options.map(option => option.value);
        setSelectAll(true);
      }
    } else {
      if (showSelectAll && typeof value === 'object') setSelectAll(options.length === value.length);
      values = typeof value === 'string' ? value.split(',') : value;
    }
    setSelectedValue(values);
    const selectedObj = values.map(value => optionsObject[value]);
    onChange(values, selectedObj, event, child);
  };

  const sortedOptions = useMemo(() => {
    if (isSortingAllowed) {
      if (isSorted) {
        return [...options].sort((a, b) => {
          const displayA = a.value || '';
          const displayB = b.value || '';
          return displayA.localeCompare(displayB);
        });
      }
      return [...options].sort((a, b) => {
        const displayA = a.display || '';
        const displayB = b.display || '';
        return displayA.localeCompare(displayB);
      });
    } else {
      return options;
    }
  }, [options, isSortingAllowed, isSorted]);

  const defaultSx = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        position: 'relative',
        backdropFilter: 'blur(8px)',
        backgroundColor: alpha(theme.palette.common.white, 0.7),
        border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: '10px',
        fontWeight: 500,
        color: theme.palette.text.primary,

        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.8),
          borderColor: alpha(theme.palette.common.white, 0.5),
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        },

        '&.Mui-focused': {
          backgroundColor: alpha(theme.palette.common.white, 0.95),
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}, 0 4px 20px ${alpha(
            theme.palette.common.black,
            0.12,
          )}`,
          backdropFilter: 'blur(12px)',
        },

        // ✅ Disabled state (added)
        '&.Mui-disabled': {
          backgroundColor: 'rgba(245, 245, 245, 0.5)',
          backdropFilter: 'blur(8px)',
          borderColor: 'rgba(200, 200, 200, 0.3)',
          cursor: 'not-allowed',

          '& .MuiOutlinedInput-input': {
            color: 'rgba(26, 26, 26, 0.5)',
            WebkitTextFillColor: 'rgba(26, 26, 26, 0.5)',
            cursor: 'not-allowed',
          },
        },

        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      },

      '& .MuiFormHelperText-root': {
        marginTop: '8px',
        fontSize: '0.8125rem',
        fontWeight: 500,
        letterSpacing: '0.3px',
      },

      '& .MuiFormLabel-root': {
        fontWeight: 500,
        fontSize: '14px',
        color: theme.palette.common.black,
        marginBottom: '6px',

        '&.Mui-required .MuiFormLabel-asterisk': {
          color: 'inherit',
          marginLeft: '4px',
        },
      },
    }),
    [theme],
  );

  const mergedSx = { ...defaultSx, ...sx };

  return (
    <Grid container sx={{ width: '100%' }}>
      {label && (
        <InputLabel
          sx={{
            mb: '8px',
            color: theme.palette.common.black,
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0.3px',
            '&.Mui-required .MuiFormLabel-asterisk': {
              color: 'inherit',
              marginLeft: '4px',
            },
          }}
          className={'sm-text'}
          id={`${id}-label`}
          required={required}
        >
          {label}
        </InputLabel>
      )}
      <Select
        disabled={disabled}
        error={props.error ? true : false}
        className="w-full"
        labelId={`${id}-label`}
        id={id}
        value={selectedValue}
        onChange={handleChange}
        name={name}
        input={<OutlinedInput notched={false} label={label} />}
        renderValue={
          renderValue ||
          function (selected: Array<string>): React.ReactNode {
            return renderSelectedValues(selected, placeholder, optionsObject, theme);
          }
        }
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        defaultOpen={defaultOpen}
        MenuProps={MenuProps}
        multiple={!!multiple}
        displayEmpty={!!placeholder}
        defaultValue={defaultValue}
        sx={{
          ...mergedSx,
          borderRadius: '12px',
          backgroundColor: 'white',
        }}
      >
        <MenuItem
          className="p-0.5"
          disabled={true}
          value=""
          sx={{
            opacity: 0.5,
            fontSize: '0.875rem',
            color: theme.palette.text.disabled,
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
        >
          Select
        </MenuItem>
        {sortedOptions.length > 1 && showSelectAll && (
          <MenuItem
            className="p-0.5"
            value="selectAll"
            sx={{
              backgroundColor: 'transparent',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              marginBottom: '4px',
              paddingBottom: '6px',
              transition: 'all 0.2s ease',

              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },

              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              },
            }}
          >
            <Checkbox
              checked={selectAll}
              sx={{
                color: theme.palette.primary.main,
                '&.Mui-checked': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <ListItemText
              primary={'Select All'}
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            />
          </MenuItem>
        )}
        {sortedOptions.map((option: DropdownOptionType, index) => {
          const { display, value, disable, grouped, secondary = '' } = option;
          return (
            <MenuItem
              className="p-0.5"
              key={`${id}-${value}-${index}`}
              value={value}
              disabled={!!disable || !!grouped}
              sx={{
                padding: '8px 12px',
                margin: '2px 4px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent',
                color: theme.palette.text.primary,

                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },

                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  fontWeight: 500,

                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.22),
                  },
                },

                '&.Mui-disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed',
                  backgroundColor: alpha(theme.palette.action.disabledBackground, 0.3),
                },
              }}
            >
              {multiple && !grouped && (
                <Checkbox
                  checked={selectedValue.indexOf(value) > -1}
                  disabled={!!disable}
                  sx={{
                    marginRight: '8px',
                    color: theme.palette.primary.main,
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                      color: theme.palette.action.disabled,
                    },
                  }}
                />
              )}
              <ListItemText
                primary={display}
                secondary={secondary}
                sx={{
                  wordBreak: 'break-word',

                  '& .MuiListItemText-primary': {
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    color: 'inherit',

                    // ✅ important
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  },

                  '& .MuiListItemText-secondary': {
                    fontSize: '0.8125rem',
                    color: theme.palette.text.secondary,
                    marginTop: '2px',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  },
                }}
              />
            </MenuItem>
          );
        })}
      </Select>
      {props.error && (
        <FormHelperText
          error
          className="m-0"
          sx={{
            marginTop: '6px',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: theme.palette.error.main,
            letterSpacing: '0.3px',
          }}
        >
          {props.error}
        </FormHelperText>
      )}
    </Grid>
  );
}

function renderSelectedValues(
  selectedValue: Array<string>,
  placeholder: string | undefined,
  optionsObject: DropdownOptionTypeObjet,
  theme: any,
): React.ReactNode {
  let values = selectedValue?.length
    ? selectedValue.reduce((op: string, value: any) => {
        const val = value?.name || optionsObject[value]?.display || '';
        op.length && (op += ', ');
        op += val;
        return op;
      }, '')
    : placeholder;
  const isPlaceholder = placeholder && (!selectedValue?.length || !values);
  if (placeholder && !values) values = placeholder;
  return (
    <Typography
      className="truncate"
      style={{
        fontSize: '0.9375rem',
        fontWeight: 500,
        color: isPlaceholder ? theme.palette.text.secondary : theme.palette.text.primary,
        letterSpacing: '0.3px',
      }}
    >
      {values}
    </Typography>
  );
}

export default SBDropdown;
